<template>
  <modal @close="$emit('close')" class-name="shipping-plan-modal-container offer-modal">
    <template slot="header">
      <h4 class="modal-title">{{ title }}</h4>
      <md-button class="md-simple md-just-icon md-round modal-default-button" @click="$emit('close')">
        <md-icon>clear</md-icon>
      </md-button>
    </template>

    <template slot="body">
      <md-tabs @md-changed="activeTab = $event" class="offer-tabs">
        <md-tab id="offer" :md-label="$t('offers.tabOffer')">
      <div v-if="isEdit" class="offer-image-manage">
        <img v-if="imageUrl" :src="imageUrl" class="offer-image-manage__preview" :alt="sku" @click="zoomUrl = imageUrl" />
        <div v-else class="offer-image-manage__empty"><md-icon>image</md-icon></div>
        <div class="offer-image-manage__actions">
          <small v-if="!imageUrl" class="offer-field-hint">{{ $t("offers.image.none") }}</small>
          <md-button v-if="imageUrl && canEditImage" class="md-simple md-sm md-danger" :disabled="imageBusy" @click="deleteImage">
            <md-icon>delete</md-icon>&nbsp;{{ $t("products.image.delete") }}
          </md-button>
          <md-button v-if="canEditImage" class="md-simple md-sm" :disabled="imageBusy" @click="$refs.imageFile.click()">
            <md-icon>upload</md-icon>&nbsp;{{ imageUrl ? $t("products.image.replace") : $t("products.image.upload") }}
          </md-button>
          <input ref="imageFile" type="file" accept=".jpg,.jpeg,.png" style="display: none" @change="uploadImage($event)" />
        </div>
      </div>
      <ValidationObserver ref="form" v-slot="{}">
        <div class="offer-sku-row">
          <ValidationProvider name="sku" rules="required" v-slot="{ passed, failed, errors }" class="offer-sku-field">
            <md-field :class="[{ 'md-error': failed || skuError }, { 'md-valid': passed && !skuError }]">
              <label>{{ $t("offers.sku") }}</label>
              <md-input v-model="sku" type="text" autocomplete="off"></md-input>
              <div class="md-error" v-if="skuError">{{ $t("offers.errors.sku_exists") }}</div>
              <div class="md-error" v-else-if="errors[0]">{{ $t(errors[0]) }}</div>
            </md-field>
          </ValidationProvider>
          <md-button class="md-icon-button md-success md-raised" :title="$t('offers.takeProductSku')" :disabled="items.length !== 1" @click="takeProductSku">
            <md-icon>content_copy</md-icon>
          </md-button>
        </div>
      </ValidationObserver>

      <div class="offer-search">
        <md-field>
          <label>{{ lookupLoading ? $t("offers.loadingProducts") : $t("offers.productSearch") }}</label>
          <md-input
            ref="search"
            v-model="query"
            type="text"
            autocomplete="off"
            :disabled="lookupLoading"
            @keydown.native.down.prevent="move(1)"
            @keydown.native.up.prevent="move(-1)"
            @keydown.native.enter.prevent="addHighlighted"
            @keydown.native.esc.stop.prevent="query = ''"
          ></md-input>
          <span class="md-helper-text">{{ $t("offers.productSearchHint") }}</span>
        </md-field>
        <ul v-if="query.trim()" class="offer-matches">
          <li v-for="(p, index) in matches" :key="p.id" :class="{ 'offer-match--active': index === highlight }" @mousedown.prevent @mouseenter="highlight = index" @click="addProduct(p)">
            <strong>{{ p.sku }}</strong>
            <span class="offer-match-name">{{ p.name }}</span>
          </li>
          <li v-if="!matches.length" class="offer-match--empty">{{ $t("offers.noProductFound") }}</li>
        </ul>
      </div>

      <table class="offer-items">
        <thead>
          <tr>
            <th>{{ $t("offers.productSku") }}</th>
            <th>{{ $t("offers.productName") }}</th>
            <th class="offer-items-qty">{{ $t("offers.quantity") }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.productId">
            <td class="offer-items-sku">{{ item.sku }}</td>
            <td class="offer-items-name" :title="item.name">{{ item.name }}</td>
            <td class="offer-items-qty">
              <md-field class="offer-qty-field">
                <md-input v-model.number="item.quantity" type="number" min="1" step="1" @blur.native="normalizeQuantity(item)"></md-input>
              </md-field>
            </td>
            <td>
              <md-button class="md-icon-button md-simple" :title="$t('offers.remove')" @click="removeItem(item)">
                <md-icon>close</md-icon>
              </md-button>
            </td>
          </tr>
          <tr v-if="!items.length">
            <td colspan="4" class="offer-items-empty">{{ $t("offers.noItems") }}</td>
          </tr>
        </tbody>
        <tfoot v-if="items.length">
          <tr>
            <td colspan="4" class="offer-items-summary">
              {{ $t("offers.summary", { positions: items.length, pieces: totalPieces }) }}
              <span class="offer-weight" :class="{ 'offer-weight--unknown': totalWeight == null }">
                {{ totalWeight == null ? $t("offers.weightUnknown") : $t("offers.weightFromProducts", { kg: $options.filters.formatNumberGerman(totalWeight, 3) }) }}
              </span>
            </td>
          </tr>
        </tfoot>
      </table>

      <div class="offer-amounts">
        <div v-for="f in amountFields" :key="f.key" class="offer-amount-wrap">
          <md-field class="offer-amount">
            <label>{{ $t("offers." + f.key) }}</label>
            <md-input v-model="amounts[f.key]" type="number" min="0" :max="f.max" step="0.01" inputmode="decimal"></md-input>
            <span class="md-suffix">{{ f.unit }}</span>
          </md-field>
          <small v-if="f.hint" class="offer-field-hint">{{ $t(f.hint) }}</small>
        </div>
        <md-field class="offer-amount offer-class">
          <label>{{ $t("offers.commissionClass") }}</label>
          <md-select v-model="amounts.commissionClass" md-dense>
            <md-option value="">{{ $t("offers.commissionClassStandard") }}{{ commissionSuffix(commissionPercents.standard) }}</md-option>
            <md-option value="Tools">{{ $t("offers.commissionClassTools") }}{{ commissionSuffix(commissionPercents.tools) }}</md-option>
          </md-select>
        </md-field>
      </div>
        </md-tab>
        <md-tab id="calculation" :md-label="$t('offers.tabCalculation')">
          <offer-calculation v-if="activeTab === 'calculation'" :sku="sku" :items="items" :amounts="amounts" :overrides="overrides" @override="setOverride"></offer-calculation>
        </md-tab>
        <md-tab id="priceHistory" :md-label="$t('offers.tabPriceHistory')">
          <price-history v-if="activeTab === 'priceHistory'" :sku="sku"></price-history>
        </md-tab>
      </md-tabs>
      <image-zoom :url="zoomUrl" @close="zoomUrl = null"></image-zoom>
    </template>

    <template slot="footer">
      <md-button class="md-secondary mr-1" @click="$emit('close')">{{ $t("offers.close") }}</md-button>
      <md-button class="md-success" :disabled="saving || !canSave" @click="save">{{ $t("offers.save") }}</md-button>
    </template>
  </modal>
</template>

<script>
import { Modal } from "@/components";
import ImageZoom from "@/components/Products/ImageZoom.vue";
import { hasPermission } from "@/mixins/auth";
import offersService from "@/services/offers.service";
import OfferCalculation from "./OfferCalculation.vue";
import PriceHistory from "@/components/PriceHistory.vue";
import shippingDataService from "@/services/shipping-data.service";
import generalCostsService from "@/services/general-costs.service";

const MAX_MATCHES = 8;
// Manuell ueberschreibbare Kalkulationswerte (leer = automatisch); gespeichert als <key>Override am Angebot
const OVERRIDE_FIELDS = [
  { key: "pickCost", max: null },
  { key: "shippingCost", max: null },
  { key: "acosPercent", max: 100 },
  { key: "fixedCostSharePercent", max: 100 },
  { key: "returnRatePercent", max: 100 },
  { key: "vatPercent", max: 100 },
];
const AMOUNT_FIELDS = [
  { key: "handlingCost", unit: "EUR" },
];
// B-Ware gehoert nicht in Produktsets: Artikelstamm-Zustand oder SKU-Konvention "_bware"
const isBware = (p) => /^b/i.test(p.condition || "") || /_bware$/i.test((p.sku || "").trim());

export default {
  components: { Modal, ImageZoom, OfferCalculation, PriceHistory },
  props: {
    // Bestehendes Angebot zum Bearbeiten; ohne Angebot wird neu angelegt
    offer: { type: Object, default: null },
    // Positionen von offer uebernehmen, aber als neues Angebot mit leerer SKU anlegen
    copy: { type: Boolean, default: false },
  },
  data() {
    return {
      sku: this.offer && !this.copy ? this.offer.sku : "",
      activeTab: "offer",
      // Provisionen der Klassen aus den Allgemeinen Kosten, fuer die Anzeige hinter der Auswahl
      commissionPercents: { standard: null, tools: null },
      overrides: Object.fromEntries(OVERRIDE_FIELDS.map((f) => [f.key, this.offer && this.offer[`${f.key}Override`] != null ? this.offer[`${f.key}Override`] : ""])),
      amountFields: AMOUNT_FIELDS,
      amounts: {
        ...Object.fromEntries(AMOUNT_FIELDS.map((f) => [f.key, this.offer && this.offer[f.key] != null ? this.offer[f.key] : ""])),
        commissionClass: (this.offer && this.offer.commissionClass) || "",
      },
      items: this.offer ? this.offer.items.map((i) => ({ productId: i.productId, sku: i.sku, name: i.name, quantity: i.quantity, weightKg: i.weightKg })) : [],
      query: "",
      highlight: 0,
      lookup: [],
      lookupLoading: true,
      saving: false,
      skuError: false,
      image: this.offer && !this.copy ? this.offer.image : null,
      imageUrl: null,
      imageBusy: false,
      zoomUrl: null,
    };
  },
  computed: {
    isEdit() {
      return !!this.offer && !this.copy;
    },
    canEditImage() {
      return hasPermission("Write", "Offers", "Edit");
    },
    title() {
      return this.isEdit ? this.$t("offers.editOffer") : this.$t("offers.createOffer");
    },
    totalPieces() {
      return this.items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
    },
    // Gesamtgewicht aus den Produkten; null, sobald bei einem Produkt das Gewicht im Artikelstamm fehlt
    totalWeight() {
      if (!this.items.length || this.items.some((i) => !(Number(i.weightKg) > 0))) return null;
      return this.items.reduce((sum, i) => sum + Number(i.weightKg) * (Number(i.quantity) || 0), 0);
    },
    amountsValid() {
      return AMOUNT_FIELDS.every((f) => {
        const v = this.amounts[f.key];
        if (v === "" || v == null) return true;
        const n = Number(v);
        return Number.isFinite(n) && n >= 0 && (f.max == null || n <= f.max);
      });
    },
    overridesValid() {
      return OVERRIDE_FIELDS.every((f) => {
        const v = this.overrides[f.key];
        if (v === "" || v == null) return true;
        const n = Number(v);
        return Number.isFinite(n) && n >= 0 && (f.max == null || n <= f.max);
      });
    },
    canSave() {
      return !!this.sku.trim() && this.amountsValid && this.overridesValid && this.items.length > 0 && this.items.every((i) => Number.isInteger(i.quantity) && i.quantity >= 1);
    },
    matches() {
      const q = this.query.trim().toLowerCase();
      if (!q) return [];
      const rank = (p) => {
        const sku = (p.sku || "").toLowerCase();
        if (sku === q || (p.gtin || "").toLowerCase() === q) return 0;
        if (sku.startsWith(q)) return 1;
        return 2;
      };
      return this.lookup
        .filter((p) => !isBware(p))
        .filter((p) => (p.sku || "").toLowerCase().includes(q) || (p.name || "").toLowerCase().includes(q) || (p.gtin || "").toLowerCase().includes(q))
        .map((p) => ({ p, r: rank(p) }))
        .sort((a, b) => a.r - b.r || (a.p.sku || "").localeCompare(b.p.sku || ""))
        .slice(0, MAX_MATCHES)
        .map((x) => x.p);
    },
  },
  watch: {
    sku(val) {
      const upper = (val || "").toUpperCase();
      if (upper !== val) this.sku = upper;
      this.skuError = false;
    },
    query() {
      this.highlight = 0;
    },
  },
  beforeDestroy() {
    if (this.imageUrl) URL.revokeObjectURL(this.imageUrl);
  },
  created() {
    this.loadImage();
    generalCostsService
      .getGeneralCosts()
      .then((costs) => {
        const value = (key) => {
          const cost = (costs || []).find((c) => c.key === key);
          return cost ? cost.value : null;
        };
        this.commissionPercents = { standard: value("DefaultCommissionPercent"), tools: value("ToolsCommissionPercent") };
      })
      .catch(() => {
        // ohne die Werte bleibt die Auswahl ohne Prozentangabe
      });
    shippingDataService
      .getProductLookup()
      .then((list) => {
        this.lookup = list;
      })
      .catch(() => {
        this.notify("danger", this.$t("offers.messages.lookupFailed"));
      })
      .finally(() => {
        this.lookupLoading = false;
        this.$nextTick(this.focusSearch);
      });
  },
  methods: {
    commissionSuffix(percent) {
      return percent == null ? "" : ` (${Number(percent).toLocaleString(this.$i18n.locale, { maximumFractionDigits: 2 })} %)`;
    },
    focusSearch() {
      const input = this.$refs.search && this.$refs.search.$el;
      if (input) input.focus();
    },
    move(delta) {
      const count = this.matches.length;
      if (!count) return;
      this.highlight = (this.highlight + delta + count) % count;
    },
    addHighlighted() {
      const p = this.matches[this.highlight];
      if (p) this.addProduct(p);
    },
    addProduct(p) {
      const existing = this.items.find((i) => i.productId === p.id);
      if (existing) {
        existing.quantity = (Number(existing.quantity) || 0) + 1;
      } else {
        this.items.push({ productId: p.id, sku: p.sku, name: p.name, quantity: 1, weightKg: p.weightKg });
      }
      this.query = "";
      this.$nextTick(this.focusSearch);
    },
    setOverride(key, value) {
      this.$set(this.overrides, key, value);
    },
    removeItem(item) {
      this.items = this.items.filter((i) => i !== item);
    },
    normalizeQuantity(item) {
      const n = Math.floor(Number(item.quantity));
      item.quantity = Number.isFinite(n) && n >= 1 ? n : 1;
    },
    takeProductSku() {
      if (this.items.length === 1) this.sku = this.items[0].sku;
    },
    loadImage() {
      if (this.imageUrl) URL.revokeObjectURL(this.imageUrl);
      this.imageUrl = null;
      if (!this.isEdit || !this.image) return Promise.resolve();
      return offersService
        .getImageBlob(this.offer.id)
        .then((blob) => {
          this.imageUrl = URL.createObjectURL(blob);
        })
        .catch(() => {});
    },
    uploadImage(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;
      this.imageBusy = true;
      offersService
        .uploadImage(this.offer.id, file)
        .then((res) => {
          this.image = res && res.fileName;
          this.$emit("image-changed");
          return this.loadImage();
        })
        .catch((e) => {
          const reason = e && e.response && e.response.data && e.response.data.reason;
          this.notify("danger", reason && this.$te(`offers.image.errors.${reason}`) ? this.$t(`offers.image.errors.${reason}`) : this.$t("offers.image.errors.upload_failed"));
        })
        .finally(() => {
          this.imageBusy = false;
        });
    },
    deleteImage() {
      this.imageBusy = true;
      offersService
        .deleteImage(this.offer.id)
        .then(() => {
          this.image = null;
          this.$emit("image-changed");
          return this.loadImage();
        })
        .catch(() => this.notify("danger", this.$t("offers.image.errors.delete_failed")))
        .finally(() => {
          this.imageBusy = false;
        });
    },
    notify(type, message) {
      this.$notify({ timeout: 2500, message, icon: "add_alert", horizontalAlign: "right", verticalAlign: "top", type });
    },
    save() {
      this.$refs.form.validate().then((success) => {
        if (!success || !this.canSave) return;
        const amounts = Object.fromEntries(AMOUNT_FIELDS.map((f) => [f.key, this.amounts[f.key] === "" || this.amounts[f.key] == null ? null : Number(this.amounts[f.key])]));
        const overrides = Object.fromEntries(OVERRIDE_FIELDS.map((f) => [`${f.key}Override`, this.overrides[f.key] === "" || this.overrides[f.key] == null ? null : Number(this.overrides[f.key])]));
        const payload = { sku: this.sku.trim(), ...amounts, commissionClass: this.amounts.commissionClass || null, ...overrides, items: this.items.map((i) => ({ productId: i.productId, quantity: i.quantity })) };
        this.saving = true;
        const request = this.isEdit ? offersService.updateOffer(this.offer.id, payload) : offersService.createOffer(payload);
        request
          .then((saved) => {
            this.notify("success", this.$t(this.isEdit ? "offers.messages.updated" : "offers.messages.created"));
            this.$emit("saved", saved);
          })
          .catch((e) => {
            const reason = e && (e.reason || (e.response && e.response.data && e.response.data.reason));
            if (reason === "sku_exists") {
              this.skuError = true;
            } else if (reason && this.$te(`offers.errors.${reason}`)) {
              this.notify("danger", this.$t(`offers.errors.${reason}`));
            } else if (!(e && e.response && e.response.status === 403)) {
              this.notify("danger", this.$t("offers.messages.saveFailed"));
            }
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
  },
};
</script>

<style scoped>
.mr-1 {
  margin-right: 15px;
}
.offer-image-manage {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 6px;
}
.offer-image-manage__preview {
  width: 72px;
  height: 72px;
  object-fit: contain;
  border-radius: 3px;
  cursor: zoom-in;
}
.offer-image-manage__empty {
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f2f2f2;
  border-radius: 3px;
}
.offer-image-manage__empty .md-icon {
  color: #c5c5c5;
}
.offer-image-manage__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}
.offer-sku-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.offer-sku-field {
  flex: 1;
}
.offer-search {
  position: relative;
}
.offer-matches {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  z-index: 20;
  margin: -14px 0 0;
  padding: 0;
  list-style: none;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
}
.offer-matches li {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 7px 12px;
  cursor: pointer;
}
.offer-match--active {
  background: #f1f5e8;
}
.offer-match--empty {
  color: #999;
  cursor: default !important;
}
.offer-matches li strong {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.offer-match-name {
  flex: 1;
  min-width: 0;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.offer-tabs >>> .md-tab {
  overflow-x: clip;
}
.offer-amounts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px 20px;
  margin-top: 14px;
}
.offer-amount {
  margin: 0;
}
.offer-amount-wrap {
  display: flex;
  flex-direction: column;
}
.offer-field-hint {
  color: #888;
  font-size: 11px;
  line-height: 1.3;
}
.offer-class {
  grid-column: span 2;
}
.offer-amount >>> input {
  min-width: 0;
  width: 100%;
}
@media (max-width: 600px) {
  .offer-amounts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
.offer-items {
  table-layout: fixed;
  width: 100%;
  margin-top: 8px;
  border-collapse: collapse;
}
.offer-items th:nth-child(1) {
  width: 130px;
}
.offer-items th:nth-child(3) {
  width: 110px;
}
.offer-items th:nth-child(4) {
  width: 48px;
}
.offer-items th {
  text-align: left;
  font-weight: 500;
  color: #888;
  font-size: 12px;
  padding: 4px 8px;
  border-bottom: 1px solid #ddd;
}
.offer-items td {
  padding: 2px 8px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}
.offer-items-sku {
  font-weight: 500;
  white-space: nowrap;
}
.offer-items-name {
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.offer-items-qty {
  width: 110px;
}
.offer-qty-field {
  margin: 0;
  padding-top: 0;
  min-height: 36px;
}
.offer-items-empty {
  color: #999;
  text-align: center;
  padding: 18px 8px !important;
}
.offer-weight {
  display: block;
  font-size: 12px;
}
.offer-weight--unknown {
  color: #b26a00;
}
.offer-items-summary {
  text-align: right;
  color: #666;
  padding-top: 8px !important;
  border-bottom: 0 none !important;
}
@media (max-width: 600px) {
  .offer-matches li {
    gap: 6px;
    padding: 7px 8px;
  }
  .offer-items-name,
  .offer-items th:nth-child(2) {
    display: none;
  }
}
</style>

<style>
/* Inhalt scrollt im Dialog, statt ueber den Bildschirmrand hinauszuragen */
.offer-modal .modal-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
</style>
